import { createDecorator } from "../../instantiation/common/registry";
import { Event } from "../../../base/common/events.js";
import { MenuId } from "../../actions/common/actions";

export const IContextMenuService = createDecorator<IContextMenuService>('contextMenuService');

export interface ContextMenuContext {
    readonly menuId: MenuId;
    readonly targets: Array<unknown>;
    readonly textSelection?: string;
    /** The ELEMENT the menu was opened on (typed loosely — this is common
     *  code). Commands that spawn anchored UI (the AI chat bubble) use it as
     *  the follow target: it is the exact thing the user pointed at, whatever
     *  surface it lives in. Hold weakly — it dies with the next re-render. */
    readonly sourceElement?: object;
    /**
     * The invocation point in the source window's CLIENT coordinates — for
     * DOM-placed UI (notifications, menus, tooltips).
     */
    readonly position?: { x: number, y: number };
    /**
     * The invocation point in SCREEN coordinates (client + window screenX/Y) —
     * for OS-window placement (`window.open`'s left/top). Both spaces are
     * prepared so consumers never convert themselves.
     */
    readonly screenPosition?: { x: number, y: number };
    /** The window the menu was invoked in (popout-safe; for anything the
     *  prepared positions don't cover). */
    readonly sourceWindow?: Window;
}
export function isContextMenuContext (thing: any): thing is ContextMenuContext {
    return thing
        && typeof thing === 'object'
        && thing.menuId instanceof MenuId
        && Array.isArray(thing.targets);
}

export interface WillShowContextMenuArguments {
    readonly context: ContextMenuContext;
} 

export interface IContextMenuService {

    readonly _serviceBrand: undefined;
    
    readonly onWillShowContextMenu: Event<WillShowContextMenuArguments>;

    getContextMenuItems(context: ContextMenuContext): Array<IContextMenuItem | IContextMenuSubItem>;

    /**
     * ASK for a menu to be shown — for surfaces that are not DOM elements of
     * ours, the 3D viewport above all: a right-click there is picked in core
     * (the editor owns the raycast and the camera), while the menu itself is
     * rendered by the UI. Products without a menu renderer simply have no
     * subscriber, and nothing happens.
     */
    requestContextMenu(context: ContextMenuContext): void;
    /** Fires when something asked for a menu — the UI renders it. */
    readonly onDidRequestContextMenu: Event<ContextMenuContext>;
}



export interface IContextMenuItem {
    readonly label: string;
    readonly icon: string | undefined;
    readonly tooltip?: string;
    readonly enabled?: boolean;
    /** Display label for this command's shortcut ("Ctrl+C"), when it has one.
     *  Undefined rather than "" so the UI can skip the gutter entirely instead
     *  of reserving a blank column beside every unbound entry. */
    readonly keybinding?: string;
    readonly action: () => void;
}
export interface IContextMenuSubItem {
    readonly label: string;
    readonly icon: string | undefined;
    readonly subItems: Array<IContextMenuItem | IContextMenuSubItem>;
    readonly enabled?: boolean;
}

export function isContextMenuSubItem(item: IContextMenuItem | IContextMenuSubItem): item is IContextMenuSubItem {
    return (item as IContextMenuSubItem).subItems !== undefined;
}
export function isContextMenuItem(item: IContextMenuItem | IContextMenuSubItem): item is IContextMenuItem {
    return (item as IContextMenuItem).action !== undefined;
}